import { encodeFunctionData } from 'viem';
import { ethers } from 'ethers';

import { abiRodNFT, abiFishingGame, abiRodRepairer } from '../config/abi.contract';
import useWrappedRPC from './useWrappedRPC';
import {
  generateTxnSignature,
  validateDepositRodTransaction,
  validateRepairRodTransaction,
  validateWithdrawRodTransaction,
} from '../services/transaction.service';
import { tokens } from './usePurchase';
import environments from '../utils/environments';
import useToken from './useToken';
import useUserStore from '../stores/user.store';

const {
  ROD_NFT_CONTRACT_ADDRESS,
  FISHING_GAME_CONTRACT_ADDRESS,
  ROD_REPAIRER_CONTRACT_ADDRESS,
  XFISH_TOKEN_CONTRACT_ADDRESS,
} = environments;

function useRod() {
  const { sendTransactionWithGas, provider } = useWrappedRPC();
  const { checkAndApproveToken, getXFISHTokenBalance } = useToken();
  const user = useUserStore((state) => state.user);

  const rodNFTContract = new ethers.Contract(ROD_NFT_CONTRACT_ADDRESS, abiRodNFT, provider);

  const depositRod = async (rodIds) => {
    console.log('depositRod', rodIds);
    try {
      const isApproved = await rodNFTContract.isApprovedForAll(user.walletAddress, FISHING_GAME_CONTRACT_ADDRESS);
      if (!isApproved) {
        const approvalData = encodeFunctionData({
          abi: abiRodNFT,
          functionName: 'setApprovalForAll',
          args: [FISHING_GAME_CONTRACT_ADDRESS, true],
        });

        await sendTransactionWithGas(
          {
            to: ROD_NFT_CONTRACT_ADDRESS,
            data: approvalData,
          },
          {
            header: 'Approve all Rod',
            description: `Approve all rod for Fishing Game`,
            buttonText: 'Approve all',
          }
        );
      }

      const data = encodeFunctionData({
        abi: abiFishingGame,
        functionName: 'stakeRod',
        args: [rodIds],
      });

      const rodIdsList = rodIds.join(', ');
      const txUiConfig = {
        header: `Deposit Rod`,
        description: `Deposit Rod IDs: ${rodIdsList}`,
        buttonText: `Deposit`,
      };

      // Construct transaction request
      const transactionRequest = {
        to: FISHING_GAME_CONTRACT_ADDRESS,
        data: data,
      };

      const { txReceipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const backendResponse = await validateDepositRodTransaction({ transactionHash: txReceipt.hash });

      return {
        success: true,
        message: 'Verify successful',
        data: backendResponse.data,
      };
    } catch (error) {
      console.error('Deposit transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const withdrawRod = async (rodIds) => {
    try {
      const data = encodeFunctionData({
        abi: abiFishingGame,
        functionName: 'unstakeRod',
        args: [rodIds],
      });

      const rodIdsList = rodIds.join(', ');
      const txUiConfig = {
        header: `Withdraw Rod`,
        description: `Withdraw Rod IDs: ${rodIdsList}`,
        buttonText: `Withdraw`,
      };

      // Construct transaction request
      const transactionRequest = {
        to: FISHING_GAME_CONTRACT_ADDRESS,
        data: data,
      };

      const { txReceipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const backendResponse = await validateWithdrawRodTransaction({ transactionHash: txReceipt.hash });

      return {
        success: true,
        message: 'Verify successful',
        data: backendResponse.data,
      };
    } catch (error) {
      console.error('Withdraw transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const repairRod = async (rodId, token) => {
    try {
      const paymentToken = tokens[token] || XFISH_TOKEN_CONTRACT_ADDRESS;
      const signatureRes = await generateTxnSignature({
        nameFunction: 'signatureRepairRod',
        params: { tokenId: rodId, paymentToken },
      });

      const { owner, reqId, tokenId, feeToken, feeAmount, deadline, nonce, signature } = signatureRes.data;

      const parsedAmount = ethers.utils.parseEther(`${feeAmount}`);

      const data = encodeFunctionData({
        abi: abiRodRepairer,
        functionName: 'repairRod',
        args: [owner, reqId, tokenId, feeToken, parsedAmount, deadline, nonce, signature],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      // Construct transaction request
      const transactionRequest = {
        to: ROD_REPAIRER_CONTRACT_ADDRESS,
        data: data,
      };

      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: user.walletAddress,
          spenderAddress: ROD_REPAIRER_CONTRACT_ADDRESS,
          amount: feeAmount,
        });
      else transactionRequest.value = parsedAmount;

      const txUiConfig = {
        header: `Repair Rod`,
        description: `Repair Rod ID: ${rodId}`,
        buttonText: `Repair`,
      };

      const { txReceipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const backendResponse = await validateRepairRodTransaction({ transactionHash: txReceipt.hash, tokenId: rodId });

      const updatedBalance = await getXFISHTokenBalance();

      return {
        success: true,
        message: 'Verify successful',
        data: {
          ...backendResponse.data,
          xFishTokenBalance: updatedBalance,
        },
      };
    } catch (error) {
      console.error('Repair transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  return { depositRod, withdrawRod, repairRod };
}

export default useRod;
